import { UserCommunication } from './userCommunication';

// A team's link to a Slack workspace. Carries no tokens: several teams may share
// one workspace, and Slack issues a single bot installation per workspace.
export interface SlackCredentialsKeys {
  teamId: string; // Partition key
  workspaceId: string; // Sort key
}

export interface SlackCredentialsFields {
  workspaceName: string;
  workspaceDomain: string;
  userIds: string[];
  createdAt: number;
  updatedAt: number;
  shouldPreserveData?: boolean;
}

export interface SlackCredentialsItem
  extends SlackCredentialsKeys,
    SlackCredentialsFields {}

// The single bot installation for a Slack workspace. The primary key IS the
// uniqueness constraint: one refresh-token chain and one renewal schedule per
// workspace, however many teams link to it.
export interface SlackWorkspaceTokensKeys {
  workspaceId: string; // Partition key
}

export interface SlackWorkspaceTokensFields {
  workspaceName: string;
  workspaceDomain: string;
  accessToken: string;
  refreshToken: string;
  schedulerName: string;
  createdAt: number;
  updatedAt: number;
  tokenAlreadyRevoked?: boolean;
}

export interface SlackWorkspaceTokensItem
  extends SlackWorkspaceTokensKeys,
    SlackWorkspaceTokensFields {}

export interface SlackUserCredentialsKeys {
  userId: string; // Partition key
  workspaceId: string; // Sort key
}

export interface SlackUserCredentialsFields {
  userSlackId: string;
  workspaceName: string;
  workspaceDomain: string;
  accessToken: string;
  refreshToken: string;
  schedulerName: string;
  createdAt: number;
  updatedAt: number;
  shouldPreserveData?: boolean;
  tokenAlreadyRevoked?: boolean;
}

export interface SlackUserCredentialsItem
  extends SlackUserCredentialsKeys,
    SlackUserCredentialsFields {}

// Client-facing projection of SlackCredentialsItem — carries no tokens.
export interface SlackTeamIntegrationView {
  teamId: string;
  workspaceId: string;
  workspaceName: string;
  createdAt: number;
  updatedAt: number;
}

// Client-facing projection of SlackUserCredentialsItem — carries no tokens.
export interface SlackUserIntegrationView {
  userId: string;
  workspaceId: string;
  workspaceName: string;
  userSlackId: string;
  createdAt: number;
  updatedAt: number;
}

export enum SlackScopeType {
  USER = 'user',
  BOT = 'bot',
}

export enum SlackChannelType {
  CHANNEL = 'channel',
  GROUP = 'group',
  IM = 'im',
  MPIM = 'mpim',
}

export const SLACK_DIRECT_CHANNEL_TYPES: ReadonlySet<SlackChannelType> =
  new Set([SlackChannelType.IM, SlackChannelType.MPIM]);

export const SLACK_GROUP_CHANNEL_TYPES: ReadonlySet<SlackChannelType> = new Set(
  [SlackChannelType.CHANNEL, SlackChannelType.GROUP, SlackChannelType.MPIM],
);

// Derived from a channel's is_ext_shared/is_org_shared/is_private — not itself persisted,
// just a naming for the UNKNOWN-is-a-card rule: an unconfirmed sharing state is never
// treated as INTERNAL.
export enum SlackChannelSharing {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
  UNKNOWN = 'UNKNOWN',
}

export interface SlackChannelResult {
  id?: string;
  name?: string;
  channelType?: SlackChannelType;
  members?: SlackUserDetailsResult[];
  lastMessage?: UserCommunication;
  isBotIn?: boolean;
  memberCount?: number;
}

export interface SlackChannelDetailsResult {
  id: string;
  memberIds?: string[];
  displayName?: string;
  type?: string;
  workspaceDomain?: string;
  refreshedAt?: number;
  // Slack's own is_ext_shared/is_org_shared/is_private, captured by getChannel. Structural
  // booleans, readable: a direct AUTO_WITH_RETRACT reply requires isExtShared === false
  // CONFIRMED in cache — undefined (unknown) is treated as a card, never as false.
  isExtShared?: boolean;
  isOrgShared?: boolean;
  isPrivate?: boolean;
}

export interface SlackUserDetailsResult {
  slackId: string;
  email?: string;
  displayName?: string;
  userId?: string;
  avatarUrl?: string;
  refreshedAt?: number;
  isBot?: boolean;
  dmChannelId?: string;
}

export type SlackWorkspaceCacheEntityType = 'CHANNEL' | 'USER' | 'BOT';

export type SlackWorkspaceCacheEntityId =
  `${SlackWorkspaceCacheEntityType}#${string}`;

export interface SlackWorkspaceCacheKeys {
  workspaceId: string; // Partition key
  entityId: SlackWorkspaceCacheEntityId; // Sort key - "CHANNEL#<id>" | "USER#<id>" | "BOT#<id>"
}

export interface SlackWorkspaceCacheFields {
  resourceId: string;
  entityType: SlackWorkspaceCacheEntityType;
  refreshedAt: number;
  // Suppresses the Slack refresh until this epoch-ms, so a lookup that keeps failing
  // is not re-attempted on every inbound message.
  refreshDeferredUntil?: number;

  // Channel fields
  channelName?: string;
  channelType?: SlackChannelType;
  memberIds?: string[];
  workspaceDomain?: string;
  // Slack's own is_ext_shared/is_org_shared/is_private — see SlackChannelDetailsResult for
  // why an unconfirmed (undefined) value is never treated as false.
  isExtShared?: boolean;
  isOrgShared?: boolean;
  isPrivate?: boolean;

  // User/Bot fields
  email?: string;
  displayName?: string;
  avatarUrl?: string;
  isBot?: boolean;
}

export interface SlackWorkspaceCacheItem
  extends SlackWorkspaceCacheKeys,
    SlackWorkspaceCacheFields {}
